/**
 * Generate LocalBusiness Schema for Taxi-98
 */
export const generateLocalBusinessSchema = () => ({
  "@context": "https://schema.org",
  "@type": "LocalBusiness",
  "@id": "https://taxi-98.com/#business",
  "name": "Taxi-98",
  "alternateName": "Taxi 98 Nassau",
  "description": "Professional airport taxi and transportation service in Nassau, Bahamas. Serving LPIA Airport, Paradise Island, and cruise ports with official government rates.",
  "url": "https://taxi-98.com",
  "telephone": "+1-242-810-7335",
  "email": "bookings@taxi-98.com",
  "priceRange": "$30-$60",
  "image": "https://taxi-98.com/images/taxi98-logo.png",
  "logo": "https://taxi-98.com/images/taxi98-logo.png",
  "address": {
    "@type": "PostalAddress",
    "streetAddress": "Nassau",
    "addressLocality": "Nassau",
    "addressRegion": "New Providence",
    "postalCode": "",
    "addressCountry": "BS"
  },
  "geo": {
    "@type": "GeoCoordinates",
    "latitude": "25.0443",
    "longitude": "-77.3504"
  },
  "areaServed": [
    {
      "@type": "City",
      "name": "Nassau",
      "sameAs": "https://en.wikipedia.org/wiki/Nassau,_Bahamas"
    },
    {
      "@type": "Place",
      "name": "Paradise Island",
      "sameAs": "https://en.wikipedia.org/wiki/Paradise_Island"
    },
    {
      "@type": "Place",
      "name": "Lynden Pindling International Airport",
      "sameAs": "https://en.wikipedia.org/wiki/Lynden_Pindling_International_Airport"
    }
  ],
  "serviceArea": {
    "@type": "GeoCircle",
    "geoMidpoint": {
      "@type": "GeoCoordinates",
      "latitude": "25.0443",
      "longitude": "-77.3504"
    },
    "geoRadius": "30000"
  },
  "hasOfferCatalog": {
    "@type": "OfferCatalog",
    "name": "Transportation Services",
    "itemListElement": [
      {
        "@type": "Offer",
        "itemOffered": {
          "@type": "Service",
          "name": "Airport Taxi Service",
          "description": "Professional taxi service from LPIA Airport to destinations across Nassau and Paradise Island"
        }
      },
      {
        "@type": "Offer",
        "itemOffered": {
          "@type": "Service",
          "name": "Cruise Port Transfer",
          "description": "Reliable transportation from Nassau cruise port to hotels and attractions"
        }
      },
      {
        "@type": "Offer",
        "itemOffered": {
          "@type": "Service",
          "name": "Paradise Island Taxi",
          "description": "Direct taxi service to Paradise Island, Atlantis, and surrounding areas"
        }
      }
    ]
  },
  "openingHoursSpecification": [
    {
      "@type": "OpeningHoursSpecification",
      "dayOfWeek": [
        "Monday",
        "Tuesday",
        "Wednesday",
        "Thursday",
        "Friday",
        "Saturday"
      ],
      "opens": "05:00",
      "closes": "23:00"
    },
    {
      "@type": "OpeningHoursSpecification",
      "dayOfWeek": "Sunday",
      "opens": "00:00",
      "closes": "23:59",
      "description": "Pre-arranged only"
    }
  ],
  "sameAs": [
    "https://www.facebook.com/taxi98nassau",
    "https://www.instagram.com/taxi98bahamas"
  ]
});

/**
 * Generate FAQ Schema
 */
export const generateFAQSchema = (faqs) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": faqs.map(faq => ({
    "@type": "Question",
    "name": faq.question,
    "acceptedAnswer": {
      "@type": "Answer",
      "text": faq.answer
    }
  }))
});

/**
 * Generate Service Schema
 */
export const generateServiceSchema = () => ({
  "@context": "https://schema.org",
  "@type": "Service",
  "@id": "https://taxi-98.com/#service",
  "serviceType": "Airport Taxi Service",
  "provider": {
    "@id": "https://taxi-98.com/#business"
  },
  "areaServed": {
    "@type": "City",
    "name": "Nassau",
    "containedInPlace": {
      "@type": "Country",
      "name": "Bahamas"
    }
  },
  "hasOfferCatalog": {
    "@type": "OfferCatalog",
    "name": "Nassau Airport Taxi Services",
    "itemListElement": [
      {
        "@type": "Offer",
        "itemOffered": {
          "@type": "Service",
          "name": "LPIA Airport Transfer",
          "description": "Direct transfer from Lynden Pindling International Airport to your destination in Nassau or Paradise Island",
          "provider": {
            "@id": "https://taxi-98.com/#business"
          }
        },
        "priceSpecification": {
          "@type": "PriceSpecification",
          "priceCurrency": "BSD",
          "price": "45.00",
          "description": "Starting from $40 BSD for LPIA to downtown Nassau"
        }
      }
    ]
  }
});

/**
 * Helper to inject schema into page
 */
export const SchemaScript = ({ schema }) => (
  <script
    type="application/ld+json"
    dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
  />
);



/**
 * Generate BlogPosting Schema (2026-06-10 — Hey Taxi!)
 * Returns a schema.org BlogPosting JSON-LD object for a single blog post.
 */
export const generateBlogPostingSchema = (post) => {
  if (!post) return null;
  const url = `https://taxi-98.com/hey-taxi/${post.slug}`;
  return {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    "@id": url,
    "headline": post.title,
    "description": post.seoDescription || post.excerpt || "",
    "image": post.imageUrl || "https://taxi-98.com/images/taxi98-logo.png",
    "datePublished": post.publishedAt || post.createdAt,
    "dateModified": post.updatedAt || post.publishedAt || post.createdAt,
    "articleSection": post.category || "Taxi-98 Updates",
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": url,
    },
    "author": {
      "@type": "Organization",
      "name": "Taxi-98",
      "url": "https://taxi-98.com",
    },
    "publisher": {
      "@type": "Organization",
      "name": "Taxi-98",
      "url": "https://taxi-98.com",
      "logo": {
        "@type": "ImageObject",
        "url": "https://taxi-98.com/images/taxi98-logo.png",
      },
    },
  };
};

/**
 * Generate Blog (collection) schema for the listing page.
 */
export const generateBlogSchema = (posts = []) => ({
  "@context": "https://schema.org",
  "@type": "Blog",
  "@id": "https://taxi-98.com/hey-taxi",
  "name": "Hey Taxi! — Taxi-98 Blog",
  "description": "Travel tips, Taxi-98 updates, Nassau guides, and local insight from the road.",
  "url": "https://taxi-98.com/hey-taxi",
  "blogPost": (posts || []).slice(0, 20).map((p) => ({
    "@type": "BlogPosting",
    "headline": p.title,
    "url": `https://taxi-98.com/hey-taxi/${p.slug}`,
    "datePublished": p.publishedAt || p.createdAt,
    "description": p.excerpt || "",
  })),
});

/**
 * Generate BreadcrumbList schema. items = [{name, url}, ...]
 */
export const generateBreadcrumbSchema = (items = []) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  "itemListElement": items.map((it, idx) => ({
    "@type": "ListItem",
    "position": idx + 1,
    "name": it.name,
    "item": it.url,
  })),
});
